package com.bikompanion.ui.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.first
import org.maplibre.compose.location.LocationState
import org.maplibre.compose.map.MapState
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeScopeSource
import com.bikompanion.domain.BoundingBox
import com.bikompanion.ui.composables.STATIONS_VISIBLE_ZOOM
import com.bikompanion.ui.composables.distanceSquaredTo
import com.bikompanion.ui.composables.serves
import kotlin.time.Duration.Companion.milliseconds

/** Roughly five kilometres — how far ahead of the viewport a network is fetched. */
private const val NetworkPrefetchMarginDegrees = 0.05

/** Reports what the camera is looking at; the model decides what that costs in requests. */
@OptIn(FlowPreview::class)
@Composable
internal fun VisibleNetworksEffect(
    cameraState: MapState,
    networks: () -> List<BikeNetwork>,
    onVisibleNetworksChange: (List<String>, Double, BoundingBox?) -> Unit,
) {
    val currentNetworks by rememberUpdatedState(networks)
    val currentOnVisibleNetworksChange by rememberUpdatedState(onVisibleNetworksChange)
    LaunchedEffect(Unit) {
        // The viewport is part of the snapshot rather than something read alongside it. The map
        // has none until it has rendered once, and that first one arrives when the style
        // finishes loading — an event that moves nothing, so the camera position it is paired
        // with is the one already reported. Read outside the snapshot, the run that finally had
        // a viewport therefore never happened: the model kept a null for the ground in view,
        // and the rack layer, which is scoped to ground rather than to networks, had nothing to
        // fetch for until something else moved the camera. Tapping through to it showed an
        // empty map indefinitely.
        snapshotFlow { Triple(cameraState.cameraPosition, cameraState.viewport, currentNetworks()) }
            .debounce(500.milliseconds)
            .collect { (camera, currentViewport, networks) ->
                val viewport = currentViewport?.visibleBounds?.toBoundingBox()
                val visibleBounds = viewport?.let {
                    BoundingBox(south = it.south, west = it.west, north = it.north, east = it.east)
                }
                // Below this zoom the map draws networks rather than stations, so there is no
                // station worth keeping fresh. The ground is still reported: the rack layer is
                // scoped to what the camera frames, not to which networks reach into it.
                if (camera.zoom < STATIONS_VISIBLE_ZOOM || networks.isEmpty()) {
                    currentOnVisibleNetworksChange(emptyList(), camera.zoom, visibleBounds)
                    return@collect
                }
                if (viewport == null) return@collect
                // A margin so a network loads just before it is panned into view. Smaller than it
                // needed to be when a network was a single point and the pad had to stand in
                // for its whole unknown extent; the coverage area now says where it reaches.
                val expandedBbox = BoundingBox(
                    south = viewport.south - NetworkPrefetchMarginDegrees,
                    west = viewport.west - NetworkPrefetchMarginDegrees,
                    north = viewport.north + NetworkPrefetchMarginDegrees,
                    east = viewport.east + NetworkPrefetchMarginDegrees,
                )

                currentOnVisibleNetworksChange(
                    networks
                        .filter { it.serves(expandedBbox) }
                        // Nearest first, so the model's budget keeps what's being looked at.
                        .sortedBy { it.distanceSquaredTo(camera.center) }
                        .map { it.id },
                    camera.zoom,
                    visibleBounds,
                )
            }
    }
}

@Composable
internal fun RatingPositionEffect(
    sheetOpen: Boolean,
    locationState: LocationState,
    cameraState: MapState,
    onRatingPositionChange: (Double, Double, BikeScopeSource) -> Unit,
) {
    val currentOnRatingPositionChange by rememberUpdatedState(onRatingPositionChange)
    // Resolved as the sheet opens rather than continuously: a bike number means nothing without
    // a network, and re-ranking under a rider who is walking would move the answer out from under
    // a choice they had already made. A real fix if there is one, otherwise where the map is
    // pointed — a rider who panned somewhere has implicitly said where they are, and the camera
    // always has a position where GPS may not.
    LaunchedEffect(sheetOpen) {
        if (!sheetOpen) return@LaunchedEffect
        val fix = locationState.lastLocation?.position
        if (fix != null) {
            currentOnRatingPositionChange(fix.latitude, fix.longitude, BikeScopeSource.Gps)
        } else {
            val camera = cameraState.cameraPosition.center
            currentOnRatingPositionChange(camera.latitude, camera.longitude, BikeScopeSource.MapCamera)
        }
    }
}
